import type { Notice } from '~/pages/authentication/components/AuthNotice'
import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { z } from 'zod'
import { BrandLogo } from '~/components/BrandLogo'
import RequiredFieldsHint from '~/components/RequiredFieldsHint'
import { MAIN_ID } from '~/components/SkipLink'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_SEND_RESET_PASSWORD } from '~/gql/auth/auth'
import AuthNotice from '~/pages/authentication/components/AuthNotice'

function ResetPassword() {
  const { t } = useTranslation()
  const [notice, setNotice] = useState<Notice | null>(null)

  const resetSchema = z.object({
    email: z.string().email({ message: t('input.email.message') }),
  })

  type ResetFormValues = z.infer<typeof resetSchema>

  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      email: '',
    },
  })

  const [sendResetPassword, { loading }] = useMutation(GQL_SEND_RESET_PASSWORD)

  const onSubmit = async (values: ResetFormValues) => {
    try {
      const { data } = await sendResetPassword({ variables: { email: values.email } })
      if (data && data.askForResetPassword) {
        setNotice({ kind: 'success', text: t('reset.password.success') })
      } else {
        setNotice({ kind: 'error', text: t('bad.reset.message') })
      }
    } catch {
      setNotice({ kind: 'error', text: t('bad.server-login.message') })
    }
  }

  return (
    <main id={MAIN_ID} className="flex min-h-dvh w-full flex-col items-center justify-center p-5">
      <Card className="sm:w-md w-full">
        <CardHeader className="gap-4">
          <BrandLogo className="h-9 w-auto" fallbackClassName="text-lg" />
          <div className="flex flex-col gap-1">
            <CardTitle as="h1" className="font-heading text-2xl tracking-tight">{t('reset.password')}</CardTitle>
            <p className="text-sm text-muted-foreground">{t('reset.password.description')}</p>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
              <AuthNotice notice={notice} />
              <RequiredFieldsHint />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <div className="grid gap-2">
                      <Label htmlFor="email" required>{t('common.email')}</Label>
                      <FormControl>
                        <Input
                          id="email"
                          aria-required
                          autoComplete="email"
                          type="email"
                          placeholder="m@example.com"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={loading} className="w-full">
                {loading && <Loader className="mr-2 size-4 animate-spin" />}
                {t('common.submit')}
              </Button>

              <div className="mt-4 text-center text-sm">
                <Link to="/" className="underline underline-offset-4">
                  {t('login.return-message')}
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </main>
  )
}

export default ResetPassword
